// format.ts
// Pure display formatters shared by every webhooks surface (pages, banner,
// summary card, sidebar). No React, no I/O, no module-level state — every
// function tolerates null / malformed input and returns a printable string,
// because the values come straight off API responses.

const DASH = "—";

function toMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

// Relative time in both directions:
//   past   → "just now" · "2m ago" · "5h ago" · "3d ago" · "4mo ago" · "2y ago"
//   future → "in a few seconds" · "in 12m" · "in 1h 52m" · "in 2d 4h"
// so callers can write `next try ${timeAgo(nextAttemptAt)}`. "" when the
// input is missing or unparseable (house idiom, CoinbaseOrdersTracker).
export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  const then = toMs(iso);
  if (then === null) return "";
  const diff = now - then;

  if (diff < 0) {
    const ahead = -diff;
    const mins = Math.floor(ahead / 60000);
    if (mins < 1) return "in a few seconds";
    if (mins < 60) return `in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) {
      const rem = mins % 60;
      return rem ? `in ${hrs}h ${rem}m` : `in ${hrs}h`;
    }
    const days = Math.floor(hrs / 24);
    const remH = hrs % 24;
    return remH ? `in ${days}d ${remH}h` : `in ${days}d`;
  }

  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 60) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

// Local absolute time with seconds — "Sep 18, 09:14:02" — plus the year
// when it is not the current one. Seconds matter: developers line these
// up against their own server logs.
export function formatAbsolute(iso: string | null | undefined): string {
  const ms = toMs(iso);
  if (ms === null) return DASH;
  const d = new Date(ms);
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleString([], opts);
}

// UTC ISO string without milliseconds — "2026-09-18T09:14:02Z". Printed
// next to the local time on detail pages as visible, copyable text.
export function formatUtc(iso: string | null | undefined): string {
  const ms = toMs(iso);
  if (ms === null) return DASH;
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
}

// "182 ms" below one second, "1.24 s" from there on.
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return DASH;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

// "812 B" · "3.4 KB" · "1.2 MB"
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return DASH;
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Created on first use, never at import time (module stays side-effect free).
let countFormatter: Intl.NumberFormat | null = null;

// Locale-grouped integer — "1,204". Pair with `tabular-nums` in the UI.
export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "0";
  if (!countFormatter) countFormatter = new Intl.NumberFormat();
  return countFormatter.format(n);
}

// Share of `part` in `total` with one decimal ("97.6%"); a clean "100%" /
// "0%" without the decimal; "—" when there is nothing to divide by.
export function formatPercent(
  part: number | null | undefined,
  total: number | null | undefined,
): string {
  if (!total || !Number.isFinite(total) || total <= 0) return DASH;
  const p = Math.min(100, Math.max(0, ((part || 0) / total) * 100));
  const fixed = p.toFixed(1);
  return `${fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed}%`;
}

// "whd_zd657…d4e5f" — keeps `head` leading and `tail` trailing characters.
// Lists use truncateMiddle(id, 8, 4); detail pages print the full id.
export function truncateMiddle(s: string | null | undefined, head = 8, tail = 4): string {
  if (!s) return "";
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${tail > 0 ? s.slice(-tail) : ""}`;
}

// Hostname of an endpoint URL ("api.acme-store.com", port included when
// non-default). Falls back to the raw string so a malformed value is still
// shown rather than swallowed.
export function hostOf(url: string | null | undefined): string {
  if (!url) return "";
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

// m:ss countdown for the 429 "Try again in 0:42" idiom. Clamps at 0:00.
export function formatCountdown(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.ceil(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}
